import React from 'react';
import { PillarScores } from '../types';

interface SpiderChartProps {
  pillar: PillarScores;
}

const CX = 140;
const CY = 140;
const R = 95;
const PEER_AVG = [0.62, 0.55, 0.58, 0.65, 0.52];
const SHORT_LABELS = ['Strategy', 'Arch.', 'Data', 'Eng.', 'Adapt.'];
const PILLAR_ORDER: Array<keyof PillarScores> = ['strategy', 'arch', 'data', 'engVel', 'org'];

const SpiderChart: React.FC<SpiderChartProps> = ({ pillar }) => {
  const angles = Array.from({ length: 5 }, (_, i) => -Math.PI / 2 + (2 * Math.PI * i) / 5);
  const polarPoint = (angle: number, radius: number) => ({
    x: CX + radius * Math.cos(angle),
    y: CY + radius * Math.sin(angle),
  });
  const pointsStr = (pts: Array<{ x: number; y: number }>) =>
    pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

  const gridRings = [0.25, 0.5, 0.75, 1].map((frac) => pointsStr(angles.map((a) => polarPoint(a, R * frac))));

  const axisEnds = angles.map((a) => polarPoint(a, R));
  const peerPoints = angles.map((a, i) => polarPoint(a, R * PEER_AVG[i]));
  const userPcts = PILLAR_ORDER.map((k) => pillar[k] / 100);
  const userPoints = angles.map((a, i) => polarPoint(a, R * userPcts[i]));
  const labelPoints = angles.map((a) => polarPoint(a, R + 18));

  return (
    <svg width="280" height="280" viewBox="0 0 280 280" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <style>{`
          .sg   { fill: none; stroke: #edf0f4; stroke-width: 1; }
          .sa   { stroke: #edf0f4; stroke-width: 1; }
          .sb   { fill: #8a97a6; fill-opacity: 0.07; stroke: #8a97a6; stroke-width: 1.5; stroke-dasharray: 5,3; }
          .sd   { fill: #f88143; fill-opacity: 0.18; stroke: #f88143; stroke-width: 2.5; stroke-linejoin: round; }
          .sdot { fill: #f88143; stroke: white; stroke-width: 2; }
          .sl   { font-family: Montserrat,sans-serif; font-size: 10px; font-weight: 700; fill: #2f3f56; }
          .ss   { font-family: Montserrat,sans-serif; font-size: 9px;  font-weight: 700; fill: #f88143; }
        `}</style>
      </defs>

      {/* Grid rings */}
      {gridRings.map((ring, i) => (
        <polygon key={i} className="sg" points={ring} />
      ))}

      {/* Axes */}
      {axisEnds.map((end, i) => (
        <line key={i} className="sa" x1={CX} y1={CY} x2={end.x.toFixed(1)} y2={end.y.toFixed(1)} />
      ))}

      {/* Benchmark */}
      <polygon className="sb" points={pointsStr(peerPoints)} />

      {/* Data polygon */}
      <polygon className="sd" points={pointsStr(userPoints)} />

      {/* Dots */}
      {userPoints.map((pt, i) => (
        <circle key={i} className="sdot" cx={pt.x.toFixed(1)} cy={pt.y.toFixed(1)} r="4.5" />
      ))}

      {/* Labels */}
      {labelPoints.map((lp, i) => {
        const anchor = lp.x < CX - 5 ? 'end' : lp.x > CX + 5 ? 'start' : 'middle';
        return (
          <React.Fragment key={i}>
            <text className="sl" x={lp.x.toFixed(1)} y={lp.y.toFixed(1)} textAnchor={anchor}>
              {SHORT_LABELS[i]}
            </text>
            <text className="ss" x={lp.x.toFixed(1)} y={(lp.y + 12).toFixed(1)} textAnchor={anchor}>
              {Math.round(pillar[PILLAR_ORDER[i]])}%
            </text>
          </React.Fragment>
        );
      })}
    </svg>
  );
};

export default SpiderChart;
